import { useEffect, useState } from "react";
import { X, Trophy } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ProfileMilestoneBanner — celebrates threshold crossings on the
 * student profile (60% → Fast-Apply ready, 100% → complete). Mirrors
 * the admin dashboard milestone banner pattern: dismissible per key,
 * hides once dismissed.
 */
export default function ProfileMilestoneBanner() {
  const [items, setItems] = useState([]);
  const [busy, setBusy]   = useState(null);
  const [ready, setReady] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/profile/completeness");
      setItems(r.data?.milestones || []);
    } catch {
      /* no session */
    } finally {
      setReady(true);
    }
  };
  useEffect(() => {
    refresh();
    // Re-check when profile is saved elsewhere so a fresh milestone
    // fires immediately.
    const on = () => refresh();
    window.addEventListener("wua:profile-updated", on);
    return () => window.removeEventListener("wua:profile-updated", on);
  }, []);

  const dismiss = async (key) => {
    setBusy(key);
    try {
      await api.post(`/profile/milestones/${key}/dismiss`, {});
      setItems(xs => xs.filter(x => x.key !== key));
    } finally { setBusy(null); }
  };

  if (!ready || !items.length) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="profile-milestone-banner">
      {items.map(m => (
        <div
          key={m.key}
          data-testid={`profile-milestone-${m.key}`}
          className="relative rounded-xl border border-emerald-300 bg-gradient-to-r from-emerald-50 to-sky-50 px-5 py-3 shadow-sm flex items-start gap-3"
        >
          <Trophy className="h-5 w-5 text-emerald-700 flex-none mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-emerald-900 leading-snug">
              {m.headline}
            </div>
            {m.detail && (
              <div className="mt-0.5 text-[12px] text-emerald-900/80 leading-snug">
                {m.detail}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => dismiss(m.key)}
            disabled={busy === m.key}
            aria-label="Dismiss"
            data-testid={`profile-milestone-dismiss-${m.key}`}
            className="flex-none p-1 rounded hover:bg-white/60 text-emerald-700 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
